import React, { useEffect, useState } from 'react';
import './css/Acp.css';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import useConfirmModal from './components/hooks/useConfirmModal';
import { formatDateView } from './utils/dateUtils';
import { formatCurrencyView } from './utils/currencyUtils';
import PageCard from './components/PageCard';
import usePermissions from './components/hooks/usePermissions';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';

function Redistribuicoes() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('redistribuicoes');
  
  // Verificação de situação do programa
  const { isProgramaAtivo } = useProgramaSituacao();
  
  // Campo único para pesquisa geral
  const [filtroGeral, setFiltroGeral] = useState('');
  const [redistribuicoesOriginais, setRedistribuicoesOriginais] = useState([]);
  const [atualizar, setAtualizar] = useState(0);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase =
    parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [itensCache, setItensCache] = useState({});
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  const showDefaultModal = (type, message) => {
    setModalData({ show: true, type: type, message: message });
  };
  const closeDefaultModal = () => {
    setModalData(prevModalData => ({ ...prevModalData, show: false }));
  };

  const buscarDescricoesItens = async redistribuicoes => {
    const itensParaBuscar = new Set();

    // Identificar quais itens precisam ter a descrição buscada
    redistribuicoes.forEach(r => {
      if (!r.descricaoItem && r.idItem) {
        itensParaBuscar.add(r.idItem);
      }
    });

    if (itensParaBuscar.size === 0) return;

    try {
      console.log('Buscando todos os itens da associação...');
      // Buscar todos os itens da associação
      const response = await axios.get(`${API_BASE_URL.ITEM}/associacao`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      });

      const todosItens = Array.isArray(response.data) ? response.data : [];
      console.log(`Total de itens da associação: ${todosItens.length}`);

      // Criar cache dos itens que precisamos
      const novoCache = {};
      itensParaBuscar.forEach(idItem => {
        // Procurar o item pelo campo 'id' (não 'idItem')
        const itemEncontrado = todosItens.find(item => item.id === idItem);

        if (itemEncontrado) {
          console.log(`Item ${idItem} encontrado:`, itemEncontrado);
          novoCache[idItem] = itemEncontrado.descricao || `Item ${idItem}`;
        } else {
          console.log(`Item ${idItem} não encontrado na lista`);
          novoCache[idItem] = `Item ${idItem}`;
        }
      });

      console.log('Cache de itens criado:', novoCache);
      setItensCache(prev => ({ ...prev, ...novoCache }));
    } catch (error) {
      console.error('Erro ao buscar descrições dos itens:', error);

      // Fallback: criar cache com valores padrão
      const cacheDefault = {};
      itensParaBuscar.forEach(idItem => {
        cacheDefault[idItem] = `Item ${idItem}`;
      });
      setItensCache(prev => ({ ...prev, ...cacheDefault }));
    }
  };

  const getDescricaoItem = redistribuicao => {
    // Se já tem a descrição no objeto, usa ela
    if (redistribuicao.descricaoItem) {
      return redistribuicao.descricaoItem;
    }

    // Se não tem, busca no cache
    if (redistribuicao.idItem && itensCache[redistribuicao.idItem]) {
      return itensCache[redistribuicao.idItem];
    }

    // Se não encontrou, retorna fallback
    return redistribuicao.idItem
      ? `Item ${redistribuicao.idItem}`
      : 'Item não especificado';
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.REDISTRIBUICAO}/por-data-ano`, {
        params: { cnpjAssociacao: cnpj, ano: parseInt(anoBase) },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        const dadosRecebidos = Array.isArray(result.data) ? result.data : [];
        console.log('dadosRecebidos:', dadosRecebidos);
        setRedistribuicoesOriginais(dadosRecebidos);

        // Buscar descrições dos itens que estão faltando
        buscarDescricoesItens(dadosRecebidos);
      })
      .catch(error => {
        console.error('Erro ao obter registros:', error);
        setRedistribuicoesOriginais([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, cnpj, navigate]);

  // Filtro dos dados (deve vir antes do uso em paginação)
  // Função para converter data YYYY-MM-DD <-> DD/MM/YYYY
  function normalizeDate(dateStr) {
    if (!dateStr) return '';
    // Se vier no formato YYYY-MM-DD, retorna DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    }
    // Se vier no formato DD/MM/YYYY, retorna YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [d, m, y] = dateStr.split('/');
      return `${y}-${m}-${d}`;
    }
    return dateStr;
  }

  const redistribuicoesFiltradas = [...redistribuicoesOriginais].filter(r => {
    if (!filtroGeral) return true;
    const termo = filtroGeral.trim().toLowerCase();

    // Valor
    const valorStr =
      r.valor !== undefined && r.valor !== null
        ? String(r.valor).replace('.', ',')
        : '';

    // Data
    const dataStr = r.data || '';
    const dataNorm = normalizeDate(dataStr);
    const termoNorm = normalizeDate(termo);

    // Pesquisa em todos os campos
    if (
      (r.descricaoPrograma || '').toLowerCase().includes(termo) ||
      (r.descricaoAcao || '').toLowerCase().includes(termo) ||
      (r.descricaoSubAcao || '').toLowerCase().includes(termo) ||
      (r.descricaoElementoDespesa || '').toLowerCase().includes(termo) ||
      (r.descricaoItem || '').toLowerCase().includes(termo) ||
      valorStr.toLowerCase().includes(termo) ||
      dataStr.toLowerCase().includes(termo) ||
      dataNorm.toLowerCase().includes(termo) ||
      dataStr.toLowerCase().includes(termoNorm)
    ) {
      return true;
    }
    return false;
  });

  const { setPagination } = usePagination10(0); // Será atualizado depois do agrupamento
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  // Agrupar redistribuições por operação
  const redistribuicoesAgrupadas = redistribuicoesFiltradas.reduce(
    (grupos, r) => {
      const operacao = r.operacao || 'sem_operacao';
      if (!grupos[operacao]) {
        grupos[operacao] = [];
      }
      grupos[operacao].push(r);
      return grupos;
    },
    {}
  );

  // Converter grupos em array e ordenar por data (mais recente primeiro)
  const gruposOrdenados = Object.entries(redistribuicoesAgrupadas)
    .map(([operacao, redistribuicoes]) => ({
      operacao,
      redistribuicoes: redistribuicoes.sort(
        (a, b) => new Date(b.data) - new Date(a.data)
      ),
      data: redistribuicoes[0]?.data || '',
      valorTotal: redistribuicoes.reduce((sum, r) => sum + (r.valor || 0), 0),
    }))
    .sort((a, b) => new Date(b.data) - new Date(a.data));

  // Paginação baseada em grupos
  const totalItems = gruposOrdenados.length;

  // Atualizar a paginação com o total correto
  const { setPagination: paginationUpdated } = usePagination10(totalItems);
  const selectedGrupos = gruposOrdenados.slice(
    paginationUpdated.startIndex,
    paginationUpdated.startIndex + paginationUpdated.rowsPerPage
  );

  const handleSelectRecord = (record, operacao) => {
    // Sempre selecionar a operação completa, não registros individuais
    const recordKey = `operacao_${operacao || record.operacao}`;

    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
      setIsProgramaInativo(false);
    } else {
      setSelectedRecord({
        key: recordKey,
        data: record,
        operacao: operacao || record.operacao,
      });
      // Verificar se o programa está encerrado (se houver idPrograma)
      if (record.idPrograma) {
        const programaAtivo = isProgramaAtivo(record.idPrograma);
        setIsProgramaInativo(!programaAtivo);
      } else {
        setIsProgramaInativo(false);
      }
    }
  };

  const getSelectedRecord = () => {
    return selectedRecord ? selectedRecord.data : null;
  };

  const hasSelectedRecord = selectedRecord !== null;

  function handleDeleteOperacao(operacaoId, anoOrcamento) {
    if (!operacaoId) return;

    axios
      .delete(
        `${API_BASE_URL.REDISTRIBUICAO}/operacao/${cnpj}/${anoOrcamento}/${operacaoId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => {
        setAtualizar(prev => prev + 1);
        showDefaultModal('success', 'A operação foi excluída com sucesso!');
        setSelectedRecord(null);
      })
      .catch(error => {
        console.error('Erro ao excluir operação:', error);
        showDefaultModal('error', 'Erro ao excluir a operação.');
      });
  }

  const handleDeleteSelected = () => {
    if (selectedRecord && selectedRecord.operacao) {
      const anoOrcamento = selectedRecord.data?.ano || anoBase;
      showConfirmModal('delete', () =>
        handleDeleteOperacao(selectedRecord.operacao, anoOrcamento)
      );
    }
  };

  const handleEditSelected = () => {
    if (selectedRecord && selectedRecord.operacao) {
      // Sempre editar operação completa
      const operacaoId = selectedRecord.operacao;
      const anoOrcamento = selectedRecord.data?.ano || anoBase;
      navigate(`/redistribuicao/operacao/${anoOrcamento}/${operacaoId}`);
    }
  };

  const handlePrintSelected = () => {
    if (!selectedRecord || !selectedRecord.operacao) return;
    const operacaoId = selectedRecord.operacao;
    const anoOrcamento = selectedRecord.data?.ano || anoBase;
    setLoading(true);
    axios
      .get(`${API_BASE_URL.REDISTRIBUICAO}/report`, {
        params: { cnpjAssociacao: cnpj, ano: anoOrcamento, operacao: operacaoId },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      .then(res => {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        const title = `Redistribuição - Operação ${operacaoId}`;
        localStorage.setItem('pdfViewerData', JSON.stringify({ url: blobUrl, title }));
        window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { url: blobUrl, title } }));
      })
      .catch(() => {
        showDefaultModal('error', 'Erro ao gerar o PDF da operação.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNewRedistribuicao = () => {
    navigate('/redistribuicao');
  };

  return (
    <PageCard
      title="Redistribuições Orçamentárias"
      icon="bi-arrow-left-right"
      scrollable={false}
      footer={totalItems > 0 ? <Pagination10 {...paginationUpdated} /> : null}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={handleNewRedistribuicao}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            onClick={handleEditSelected}
            disabled={!podeEditar || !hasSelectedRecord || isProgramaInativo}
            title={!podeEditar ? 'Você não tem permissão para editar' : !hasSelectedRecord ? 'Selecione uma operação' : isProgramaInativo ? 'Programa ENCERRADO - não é possível editar' : 'Editar operação completa'}
          >
            <i className="bi bi-pencil me-1" />
            Editar Operação
          </button>
          <div className="vr mx-1" />
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            onClick={handleDeleteSelected}
            disabled={!podeExcluir || !hasSelectedRecord || isProgramaInativo}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !hasSelectedRecord ? 'Selecione uma operação' : isProgramaInativo ? 'Programa ENCERRADO - não é possível excluir' : 'Excluir operação completa'}
          >
            <i className="bi bi-trash me-1" />
            Excluir Operação
          </button>
          <button
            className="btn btn-secondary btn-sm d-inline-flex align-items-center"
            onClick={handlePrintSelected}
            disabled={!hasSelectedRecord}
            title="Imprimir operação completa"
          >
            <i className="bi bi-printer me-1" />
            Imprimir Operação
          </button>

          <div className="ms-auto d-flex align-items-center">
            <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
              <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                placeholder="Pesquisar..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <LoadingModal loading={isLoading} />

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle">Operação / Data</th>
                <th className="px-2 align-middle">Tipo</th>
                <th className="px-2 align-middle">Programa / Ação</th>
                <th className="px-2 align-middle">Item / Elemento</th>
                <th
                  className="px-2 align-middle text-end"
                  style={{ width: '12%' }}
                >
                  Valor
                </th>
                <th className="px-2 align-middle" style={{ width: '15%' }}>
                  Justificativa
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                selectedGrupos.map(grupo => {
                  const operacaoKey = `operacao_${grupo.operacao}`;
                  const isOperacaoSelected =
                    selectedRecord && selectedRecord.key === operacaoKey;

                  return (
                    <React.Fragment key={grupo.operacao}>
                      {/* Cabeçalho do grupo da operação */}
                      <tr
                        className={`table-info ${
                          isOperacaoSelected ? 'table-primary' : ''
                        }`}
                        style={{
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          backgroundColor: isOperacaoSelected
                            ? '#e3f2fd'
                            : '#d1ecf1',
                          borderLeft: isOperacaoSelected
                            ? '4px solid #0d6efd'
                            : 'none',
                        }}
                        onClick={() =>
                          handleSelectRecord(
                            grupo.redistribuicoes[0],
                            grupo.operacao
                          )
                        }
                      >
                        <td className="px-2">
                          <i className="bi bi-arrow-left-right me-2 text-primary"></i>
                          <strong>Operação #{grupo.operacao}</strong>
                          <br />
                          <small className="text-muted">
                            {formatDateView(grupo.data)}
                          </small>
                        </td>
                        <td className="px-2">
                          <span className="badge bg-info">Redistribuição</span>
                        </td>
                        <td className="px-2">
                          <small className="text-muted">
                            {grupo.redistribuicoes.length} movimento
                            {grupo.redistribuicoes.length > 1 ? 's' : ''}
                          </small>
                        </td>
                        <td className="px-2">
                          <small className="text-muted">Agrupado</small>
                        </td>
                        <td className="px-2 text-end">
                          <strong
                            className={
                              grupo.valorTotal === 0
                                ? 'text-success'
                                : grupo.valorTotal > 0
                                ? 'text-success'
                                : 'text-danger'
                            }
                          >
                            {formatCurrencyView(grupo.valorTotal)}
                          </strong>
                        </td>
                        <td className="px-2">
                          <div
                            className="text-muted"
                            style={{ fontSize: '0.85em' }}
                          >
                            <strong>Justificativa:</strong>
                            <br />
                            {grupo.redistribuicoes[0]?.justificativa?.substring(
                              0,
                              60
                            )}
                            {grupo.redistribuicoes[0]?.justificativa?.length >
                            60
                              ? '...'
                              : ''}
                          </div>
                        </td>
                      </tr>

                      {/* Detalhes de cada redistribuição da operação */}
                      {grupo.redistribuicoes.map((r, index) => {
                        const recordKey = `${r.ano}-${r.cnpjAssociacao}-${r.idPrograma}-${r.idAcao}-${r.idElementoDespesa}-${r.idItem}-${r.data}-${r.operacao}`;
                        const isSelected =
                          selectedRecord && selectedRecord.key === recordKey;

                        const isDebito = r.valor < 0;
                        const rowStyle = {
                          cursor: 'pointer',
                          backgroundColor: isSelected
                            ? '#f8f9fa'
                            : 'transparent',
                          borderLeft: isSelected
                            ? '3px solid #28a745'
                            : `3px solid ${isDebito ? '#dc3545' : '#28a745'}`,
                          marginLeft: '10px',
                        };

                        return (
                          <tr
                            key={recordKey}
                            className={`${isSelected ? 'table-active' : ''}`}
                            style={rowStyle}
                            onClick={() =>
                              handleSelectRecord(r, grupo.operacao)
                            }
                          >
                            <td className="px-2 ps-4">
                              <small className="text-muted">
                                ↳ Mov. {index + 1}
                              </small>
                            </td>
                            <td className="px-2">
                              <span
                                className={`badge ${
                                  isDebito ? 'bg-danger' : 'bg-success'
                                }`}
                              >
                                {isDebito ? 'DÉBITO' : 'CRÉDITO'}
                              </span>
                            </td>
                            <td className="px-2">
                              <div style={{ fontSize: '0.85em' }}>
                                <strong>{r.descricaoPrograma}</strong>
                                <br />
                                <span className="text-muted">
                                  {r.descricaoAcao}
                                </span>
                                {r.descricaoSubAcao && (
                                  <>
                                    <br />
                                    <small className="text-muted">
                                      Sub: {r.descricaoSubAcao}
                                    </small>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-2">
                              <div style={{ fontSize: '0.85em' }}>
                                <strong>{getDescricaoItem(r)}</strong>
                              </div>
                            </td>
                            <td className="px-2 text-end">
                              <span
                                className={`fw-bold ${
                                  isDebito ? 'text-danger' : 'text-success'
                                }`}
                              >
                                {formatCurrencyView(r.valor)}
                              </span>
                            </td>
                            <td className="px-2">
                              <small className="text-muted">-</small>
                            </td>
                          </tr>
                        );
                      })}

                      {/* Linha separadora entre operações */}
                      <tr style={{ height: '5px' }}>
                        <td
                          colSpan="6"
                          style={{ padding: '2px', backgroundColor: '#f8f9fa' }}
                        ></td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              {!isLoading && redistribuicoesOriginais.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />
      <ConfirmModal {...setConfirmModal} />
    </div>
  </PageCard>
  );
}

export default Redistribuicoes;
